export const DATE_FORMAT_STORAGE_KEY = 'dateFormat'

export const DATE_FORMATS = ['yyyy-mm-dd', 'dd/mm/yyyy', 'mm/dd/yyyy', 'dd.mm.yyyy'] as const

export type DateFormat = typeof DATE_FORMATS[number]

export const DATE_FORMAT_LABELS: Record<DateFormat, string> = {
    'yyyy-mm-dd': 'YYYY-MM-DD',
    'dd/mm/yyyy': 'DD/MM/YYYY',
    'mm/dd/yyyy': 'MM/DD/YYYY',
    'dd.mm.yyyy': 'DD.MM.YYYY',
}

const DEFAULT_DATE_FORMAT: DateFormat = 'yyyy-mm-dd'

export const TIME_FORMAT_STORAGE_KEY = 'timeFormat'

export const TIME_FORMATS = ['automatic', '24h', '12h'] as const

export type TimeFormat = typeof TIME_FORMATS[number]

export const TIME_FORMAT_LABELS: Record<Exclude<TimeFormat, 'automatic'>, string> = {
    '24h': 'HH:mm',
    '12h': 'h:mm AM/PM',
}

const DEFAULT_TIME_FORMAT: TimeFormat = 'automatic'

export type DateTimeInput = { toDate: () => Date } | Date | string | number | null | undefined

type DateFormatListener = (format: DateFormat) => void
type TimeFormatListener = (format: TimeFormat) => void

const dateFormatListeners = new Set<DateFormatListener>()
const timeFormatListeners = new Set<TimeFormatListener>()

export const isDateFormat = (value: string | null): value is DateFormat =>
    value === 'yyyy-mm-dd' || value === 'dd/mm/yyyy' || value === 'mm/dd/yyyy' || value === 'dd.mm.yyyy'

export const getDateFormat = (): DateFormat => {
    if (typeof window === 'undefined') return DEFAULT_DATE_FORMAT
    const stored = localStorage.getItem(DATE_FORMAT_STORAGE_KEY)
    return isDateFormat(stored) ? stored : DEFAULT_DATE_FORMAT
}

export const setDateFormat = (format: DateFormat) => {
    localStorage.setItem(DATE_FORMAT_STORAGE_KEY, format)
    dateFormatListeners.forEach((listener) => listener(format))
}

export const subscribeDateFormat = (listener: DateFormatListener) => {
    dateFormatListeners.add(listener)
    return () => {
        dateFormatListeners.delete(listener)
    }
}

export const isTimeFormat = (value: string | null): value is TimeFormat =>
    value === 'automatic' || value === '24h' || value === '12h'

export const getTimeFormat = (): TimeFormat => {
    if (typeof window === 'undefined') return DEFAULT_TIME_FORMAT
    const stored = localStorage.getItem(TIME_FORMAT_STORAGE_KEY)
    return isTimeFormat(stored) ? stored : DEFAULT_TIME_FORMAT
}

export const setTimeFormat = (format: TimeFormat) => {
    localStorage.setItem(TIME_FORMAT_STORAGE_KEY, format)
    timeFormatListeners.forEach((listener) => listener(format))
}

export const subscribeTimeFormat = (listener: TimeFormatListener) => {
    timeFormatListeners.add(listener)
    return () => {
        timeFormatListeners.delete(listener)
    }
}

const pad = (value: number) => String(value).padStart(2, '0')

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

export const toDate = (value: DateTimeInput): Date | null => {
    if (value == null || value === '') return null
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value
    }
    if (typeof value === 'number') {
        const date = new Date(value)
        return Number.isNaN(date.getTime()) ? null : date
    }
    if (typeof value === 'string') {
        const dateOnly = value.match(DATE_ONLY)
        if (dateOnly) {
            const date = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
            return Number.isNaN(date.getTime()) ? null : date
        }
        const date = new Date(value)
        return Number.isNaN(date.getTime()) ? null : date
    }
    if (typeof value === 'object' && typeof value.toDate === 'function') {
        const date = value.toDate()
        return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null
    }
    return null
}

export const formatDate = (date: Date, format: DateFormat = getDateFormat()): string => {
    if (Number.isNaN(date.getTime())) return ''
    const year = String(date.getFullYear())
    const month = pad(date.getMonth() + 1)
    const day = pad(date.getDate())
    switch (format) {
        case 'dd/mm/yyyy':
            return `${day}/${month}/${year}`
        case 'mm/dd/yyyy':
            return `${month}/${day}/${year}`
        case 'dd.mm.yyyy':
            return `${day}.${month}.${year}`
        default:
            return `${year}-${month}-${day}`
    }
}

export const formatTime = (date: Date, format: TimeFormat = getTimeFormat()): string => {
    if (Number.isNaN(date.getTime())) return ''
    if (format === 'automatic') {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
    const hours = date.getHours()
    const minutes = pad(date.getMinutes())
    if (format === '12h') {
        const period = hours >= 12 ? 'PM' : 'AM'
        const hour12 = hours % 12 || 12
        return `${hour12}:${minutes} ${period}`
    }
    return `${pad(hours)}:${minutes}`
}
